import { z } from "zod";
import {
  SITE_APP_TYPE_EXTERNAL_SITE,
  isChatProfileEnabled,
} from "./SiteAppType";

const translationPath = "page.register.applications.errors.";

export const CHAT_MIN_WIDTH = 320;
export const CHAT_MAX_WIDTH = 800;
export const CHAT_MIN_HEIGHT = 360;
export const CHAT_MAX_HEIGHT = 900;

export const HEX_COLOR = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const typeSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().optional().nullable(),
    description: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "type" }
);

const statusSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "status_type" }
);

const genericSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
});

const mediaFileSchema = z.object(
  {
    mediaType: genericSchema.optional(),
    id: z.string().min(1, translationPath + "file"),
    mediaTypeId: z.string().optional(),
    isPublic: z.boolean().optional(),
    fileName: z.string().optional(),
    cdnDomain: z.string().optional(),
    filePath: z.string().optional(),
    mimeType: z.string().optional(),
    fileSize: z.number().optional(),
  },
  { required_error: translationPath + "file" }
);

const siteAppMediaFilesSchema = z
  .object({
    mediaType: genericSchema,
    file: mediaFileSchema,
  })
  .superRefine((val, ctx) => {
    if (val.file.id.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["file"],
        message: translationPath + "file",
      });
    }
  });

const siteAppDomainTypeSchema = z.object(
  {
    id: z.string(),
    name: z.string().optional().nullable(),
    value: z.string().optional().nullable(),
    code: z.string().optional().nullable(),
    description: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "type" }
);

const domainsSchema = z.object({
  id: z.string().optional(),
  siteAppDomainType: siteAppDomainTypeSchema.optional().nullable(),
  domain: z.string().min(1, translationPath + "domain"),
});

const ticketServiceSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional().nullable(),
});

export const registerSchema = z
  .object({
    id: z.string().optional(),
    name: z.string().min(1, translationPath + "name"),
    domains: z.array(domainsSchema).optional().nullable(),
    type: typeSchema,
    status: statusSchema,
    frontConfig: z.string().optional().nullable(),
    description: z.string().min(1, translationPath + "description"),
    siteAppMediaFiles: z.array(siteAppMediaFilesSchema),
    storeLink: z.string().nullish(),
    fullScreenMode: z.boolean().optional(),
    chatTicketService: ticketServiceSchema.nullish(),
    chatTitle: z.string().nullish(),
    chatSubtitle: z.string().nullish(),
    chatBotName: z.string().nullish(),
    chatWidth: z.coerce.number().nullish(),
    chatHeight: z.coerce.number().nullish(),
    chatPrimaryColor: z.string().nullish(),
    chatSecondaryColor: z.string().nullish(),
    chatLogo: mediaFileSchema.nullish(),
    chatBotAvatar: mediaFileSchema.nullish(),
  })
  .superRefine((val, ctx) => {
    if (isChatProfileEnabled(val.type?.code, val.chatTicketService)) {
      if (
        val.chatWidth &&
        (val.chatWidth < CHAT_MIN_WIDTH || val.chatWidth > CHAT_MAX_WIDTH)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["chatWidth"],
          message: `${translationPath + "chat_width"}`,
        });
      }
      if (
        val.chatHeight &&
        (val.chatHeight < CHAT_MIN_HEIGHT || val.chatHeight > CHAT_MAX_HEIGHT)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["chatHeight"],
          message: `${translationPath + "chat_height"}`,
        });
      }
      if (val.type.code === SITE_APP_TYPE_EXTERNAL_SITE) {
        (["chatPrimaryColor", "chatSecondaryColor"] as const).forEach(
          (field) => {
            const color = val[field];
            if (color && !HEX_COLOR.test(color)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [field],
                message: `${translationPath + "chat_color"}`,
              });
            }
          }
        );
      }
    }
    if (
      (val.type.code === "1" ||
        val.type.code === SITE_APP_TYPE_EXTERNAL_SITE) &&
      !val.domains?.length
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["domains"],
        message: `${translationPath + "domains"}`,
      });
    }
    if (val.type.code === "1") {
      if (val.frontConfig?.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["frontConfig"],
          message: `${translationPath + "front_config"}`,
        });
      }
    }
  });

export type TRegisterForm = z.infer<typeof registerSchema>;
